import React from 'react';

import { Placement } from '@floating-ui/core';
import {
  arrow,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from '@floating-ui/react-dom';

import { PopperArrow } from '~shared/components/Popper/PopperArrow';
import { Portal } from '~shared/components/Portal';

import { useClick, useContext, useHover } from './hooks';
import { mergedRefs } from './mergedRefs';

const DEFAULT_OFFSET = 6;
export type Direction = Placement;
export type Mode = 'click' | 'hover' | 'context';
type Children = React.ReactNode & { ref?: HTMLElement };
interface PopperProps {
  children: Children; // since the children can be anything, and therefore the ref can be any element
  direction?: Direction;
  content: React.ReactNode | ((closePopper: () => void) => React.ReactNode);
  mode?: Mode;
  isDisabled?: boolean;
  enableArrow?: boolean;
  delay?: number;
  enablePersistence?: boolean;
  keyboardNavigable?: boolean;
  theme: 'light' | 'dark';
}

/**
 * Very useful wrapper for handling popper related things
 * -> tooltips, dropdowns, popovers, menus, etc.
 * Handles all mounting, unmounting, positioning, listeners, etc.
 * Recommended to not be used directly, as it has no styling.
 * Use it as part of the render method of the component of choice.
 *
 * If you wish to use a arrow with your usage of the popper, provide a arrowRef prop to the popper.
 * This ref will then be used to create the arrow for you, popper side, so don't worry about it.
 */
export function Popper({
  theme,
  children,
  direction = 'top-end',
  content,
  mode = 'click',
  isDisabled = false,
  enableArrow = false,
  delay = 0,
  enablePersistence = false,
  keyboardNavigable = false,
}: PopperProps): React.ReactElement {
  // general visibility state
  const [isVisible, setIsVisible] = React.useState(false);
  // arrow ref?
  const arrowRef = React.useRef<null | HTMLDivElement>(null);
  const defaultRef = React.useRef<null>(null);
  const resolvedRef = enableArrow ? arrowRef : defaultRef;
  // establish general positioning
  const {
    x,
    y,
    floating,
    refs,
    update,
    reference,
    middlewareData: { arrow: { x: arrowX, y: arrowY } = {} },
    placement,
  } = useFloating({
    placement: direction,
    middleware: [
      shift(),
      offset(DEFAULT_OFFSET),
      flip(),
      size({
        apply({ availableWidth, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxWidth: `${availableWidth}px`,
            maxHeight: `${availableHeight}px`,
          });
        },
      }),
      arrow({ element: resolvedRef }),
    ],
  });
  // click manager setup
  const { clickManager } = useClick({ refs, setIsVisible: setIsVisible });
  const { hoverManager } = useHover({
    refs,
    setIsVisible: setIsVisible,
    delay,
    enablePersistence,
  });
  const { contextManager } = useContext({
    refs,
    reference,
    isVisible: isVisible,
    setIsVisible: setIsVisible,
  });
  // context base reference
  React.useLayoutEffect(() => {
    window.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [update]);
  // mounting the managers
  React.useLayoutEffect(() => {
    {
      mode === 'click' && clickManager();
    }
    {
      mode === 'hover' && hoverManager();
    }
    {
      mode === 'context' && contextManager();
    }
  }, [clickManager, contextManager, hoverManager, mode]);

  // custom destroyer function for callbacks (removes any set focus in the modal
  const closePopper = () => {
    setIsVisible(false);
    (document.activeElement as HTMLElement).blur();
  };
  // when the modal is open, and keyboard accessible is enabled, you can search for things
  const [search, setSearch] = React.useState('');
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch('');
    }, 500);
    return () => clearTimeout(timeout);
  }, [search]);
  const activateSearch = React.useCallback((key: string) => {
    setSearch((state) => state + key);
  }, []);
  // when you type any keys, override the default browser behaviour
  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      // Only prevent default behavior if keyboard navigation is enabled
      if (!keyboardNavigable) return;

      e.preventDefault();
      e.stopPropagation();

      // prevent constant type coercion
      const activeElement = document.activeElement as HTMLElement;

      // forward tab handler that jumps over tabIndex -1 and stops at boundary
      const tabNext = () => {
        const siblings = Array.from(
          activeElement.parentNode.children
        ) as HTMLElement[];
        const currentIndex = siblings.indexOf(activeElement);
        const nextIndex = currentIndex + 2;
        if (nextIndex > siblings.length) return;
        if (siblings[currentIndex + 1].tabIndex === -1) {
          for (let i = currentIndex + 1; i < siblings.length; i++) {
            // if element is tabbable
            if ((siblings[i] as HTMLElement).tabIndex === 0) {
              (siblings[i] as HTMLElement).focus();
              break;
            }
          }
          return;
        }
        (activeElement.nextElementSibling as HTMLElement).focus();
      };

      // backwards tab handler that jumps over tabIndex -1 and stops at boundary
      const tabBack = () => {
        const siblings = Array.from(
          activeElement.parentNode.children
        ) as HTMLElement[];
        const currentIndex = siblings.indexOf(activeElement);
        const prevIndex = currentIndex - 1;
        if (prevIndex < 1) return;
        if (siblings[currentIndex - 1].tabIndex === -1) {
          for (let i = currentIndex - 1; i > 0; i--) {
            // if element is tabbable
            if ((siblings[i] as HTMLElement).tabIndex === 0) {
              (siblings[i] as HTMLElement).focus();
              break;
            }
          }
          return;
        }
        (activeElement.previousElementSibling as HTMLElement).focus();
      };

      switch (e.key) {
        case 'Tab':
        case 'ArrowDown': {
          tabNext();
          break;
        }
        case 'ArrowUp': {
          tabBack();
          break;
        }
        case 'Escape': {
          closePopper();
          break;
        }
        // case 'Spacebar': case ' ': are omitted since they interfere with the search ability
        case 'Enter': {
          (document.activeElement as HTMLElement).click();
          break;
        }
        default:
          activateSearch(e.key);
      }
    },
    [activateSearch, keyboardNavigable]
  );

  // in order to prevent the first item being focused by default, create a hidden element
  // this element will not be tabbable back, and just serves as the initial focus state
  const { current } = refs.floating;
  React.useEffect(() => {
    if (current === null || !keyboardNavigable) return;
    const placeholder = document.createElement('div');
    placeholder.style.cssText = `
      height: 0;
      width: 0;
      outline: none !important;
      position: absolute;
      top: 0;
      left: 0;
    `;
    placeholder.setAttribute('tabindex', '-1');
    current.children[0].insertBefore(
      placeholder,
      current.children[0].firstChild
    );
    placeholder.focus();

    // ensure that the search is reset at the end of each render
    // also cleanup possible lingering dom node
    return () => {
      setSearch('');
      placeholder.remove();
    };
  }, [current, keyboardNavigable]);

  // search and matching logic
  // searches the data-key attribute values since children can be composed components
  // reset search after 200ms of stale key
  React.useEffect(() => {
    if (current === null || !keyboardNavigable || !search) return;
    // get all the possible search matches via the data-key attribute
    const children = Array.from(current.children[0].children);
    const options = children.map((item: HTMLElement, i: number) => {
      if (i === 0) return;
      if (item.tabIndex === -1) return;
      return item.getAttribute('data-key');
    });
    // find and match if any of the options begins with the current search query
    const result = options.find(
      (element) =>
        search &&
        element &&
        element.toLowerCase().includes(search.toLowerCase())
    );
    // if there is a result, focus it
    if (result) {
      (children[options.indexOf(result)] as HTMLElement).focus();
    }
  }, [current, keyboardNavigable, search]);

  return (
    <>
      {React.isValidElement(children) &&
        React.cloneElement(children, {
          ref: mergedRefs(refs.reference, children.ref),
        })}
      {isVisible && !isDisabled ? (
        <Portal>
          <div
            ref={floating}
            tabIndex={-1}
            className="flex flex-col"
            onKeyDown={onKeyDown}
            style={{
              position: 'absolute',
              zIndex: 99999,
              top: y ?? '',
              left: x ?? '',
            }}
          >
            {typeof content === 'function' ? content(closePopper) : content}
            {enableArrow ? (
              <PopperArrow
                theme={theme}
                arrowX={arrowX}
                arrowY={arrowY}
                placement={placement}
                ref={resolvedRef}
              />
            ) : null}
          </div>
        </Portal>
      ) : null}
    </>
  );
}
